import { useRef, useEffect } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

interface MagneticCardProps {
  children: React.ReactNode;
  /** How far in px the cursor is detected (default 180) */
  radius?: number;
  /** Pull strength — fraction of offset applied (default 0.28) */
  strength?: number;
}

/**
 * Wraps children in a div that magnetically drifts toward the cursor
 * whenever the pointer comes within `radius` pixels of the element.
 * Springs back smoothly when the cursor leaves.
 */
const MagneticCard = ({
  children,
  radius = 180,
  strength = 0.28,
}: MagneticCardProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);

  const x = useSpring(rawX, { stiffness: 180, damping: 18, mass: 0.8 });
  const y = useSpring(rawY, { stiffness: 180, damping: 18, mass: 0.8 });

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < radius) {
        // Pull factor: strongest near radius edge, zero at center boundary
        const factor = (1 - dist / radius) * strength;
        rawX.set(dx * factor);
        rawY.set(dy * factor);
      } else {
        rawX.set(0);
        rawY.set(0);
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    return () => window.removeEventListener("mousemove", onMouseMove);
  }, [radius, strength, rawX, rawY]);

  return (
    <motion.div ref={ref} style={{ x, y, width: "100%", display: "flex" }}>
      {children}
    </motion.div>
  );
};

export default MagneticCard;
